import { observer } from 'mobx-react-lite';
import { sessionsStore } from '../stores';
import React from 'react';
import { reaction } from 'mobx';

/**
 * Основной хук для работы с SessionsStore
 * @returns {SessionsStore} Экземпляр store
 */
export const useSessions = () => {
  return sessionsStore;
};

/**
 * Хук для получения данных сессий с автоматической загрузкой
 * @param {boolean} autoFetch - Автоматически загружать данные при монтировании
 * @returns {Object} Объект с данными сессий и состоянием загрузки
 */
export const useSessionsData = (autoFetch = true) => {
  const store = useSessions();
  
  React.useEffect(() => {
    if (autoFetch && store.shouldFetch) {
      store.fetchSessions().catch(console.error);
    }
  }, [store, autoFetch]);

  return {
    sessions: store.sessions,
    isLoading: store.isLoading,
    error: store.error,
    hasData: store.hasData
  };
};

/**
 * Хук для получения конкретной сессии по ID
 * @param {string|number} sessionId - ID сессии
 * @returns {Object|null} Объект сессии или null
 */
export const useSessionById = (sessionId) => {
  const store = useSessions();
  
  React.useEffect(() => {
    if (store.shouldFetch) {
      store.fetchSessions().catch(console.error);
    }
  }, [store]);

  return store.getSessionById(sessionId);
};

/**
 * Хук для поиска сессий
 * @param {string} searchTerm - Поисковый запрос
 * @returns {Array} Массив найденных сессий
 */
export const useSessionsSearch = (searchTerm) => {
  const store = useSessions();
  
  React.useEffect(() => {
    if (store.shouldFetch) {
      store.fetchSessions().catch(console.error);
    }
  }, [store]);

  return store.searchSessions(searchTerm);
};

/**
 * Хук для подписки на изменения данных сессий
 * @param {Function} callback - Функция обратного вызова
 * @param {Array} dependencies - Зависимости для useEffect
 */
export const useSessionsSubscription = (callback, dependencies = []) => {
  const store = useSessions();
  
  React.useEffect(() => {
    const disposer = reaction(
      () => ({
        sessions: store.sessions,
        hasData: store.hasData,
        lastFetchTime: store.lastFetchTime
      }),
      (data) => {
        callback(data);
      },
      { fireImmediately: true }
    );
    
    return disposer;
  }, [store, callback, ...dependencies]);
};

/**
 * Хук для управления загрузкой данных
 * @returns {Object} Методы для управления загрузкой
 */
export const useSessionsActions = () => {
  const store = useSessions();
  
  return {
    fetchSessions: (forceRefresh = false) => store.fetchSessions(forceRefresh),
    refreshSessions: () => store.refreshSessions(),
    clearCache: () => store.clearCache(),
    reset: () => store.reset()
  };
};

/**
 * HOC для оборачивания компонентов в observer
 * @param {React.Component} Component - Компонент для оборачивания
 * @returns {React.Component} Обернутый компонент
 */
export const withSessions = (Component) => {
  return observer(Component);
};
